import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchProfile, startSignIn } from "@/lib/auth/oauth";
import {
  requestMobileOtp,
  verifyMobileOtp,
  fetchCurrentUser,
  logoutUser,
  submitCompleteProfile,
  submitCategoryPreferences,
  fetchOnboardingProgress,
} from "@/lib/api/otpAuth";

const SESSION_KEY = "rivyou_web_session";
const AuthContext = createContext(null);

function readStoredSession() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY) || window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredSession(session) {
  try {
    if (session) {
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      window.localStorage.removeItem(SESSION_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // Ignore storage quota errors
  }
}

/**
 * Unified Authentication Context supporting:
 * 1. Mobile OTP Authentication (aligned with mobile app endpoints)
 * 2. Complete Profile & Category Preferences Onboarding Pipeline
 * 3. Google OAuth PKCE SSO (when configured on server)
 * 4. Global Auth Modal management across the application with returnTo preservation
 */
export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authReturnTo, setAuthReturnTo] = useState(null);

  // Restore stored session on mount
  useEffect(() => {
    const stored = readStoredSession();
    if (stored?.accessToken) {
      setAccessToken(stored.accessToken);
      setRefreshToken(stored.refreshToken || null);
      setUser(stored.user || null);
    }
    setIsLoading(false);
  }, []);

  const openAuthModal = useCallback((options = {}) => {
    if (options?.returnTo) {
      setAuthReturnTo(options.returnTo);
    }
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  /**
   * Primary entry point for "Log in" / "Sign in":
   * Opens the interactive Auth Modal with Mobile OTP + Google options.
   * Accepts optional { returnTo: string }.
   */
  const signIn = useCallback(
    (options = {}) => {
      openAuthModal(options);
    },
    [openAuthModal],
  );

  /** Direct trigger for Google OAuth PKCE flow. */
  const signInWithGoogle = useCallback(
    (options = {}) => {
      const returnTo = options.returnTo || authReturnTo || window.location.pathname;
      return startSignIn({ ...options, returnTo });
    },
    [authReturnTo],
  );

  /** Requests OTP for a 10-digit mobile number. */
  const sendOtp = useCallback(async (mobileNumber) => {
    return requestMobileOtp(mobileNumber);
  }, []);

  /** Verifies 6-digit OTP code and establishes an authenticated session. */
  const verifyOtp = useCallback(
    async (mobileNumber, otpCode) => {
      setIsLoading(true);
      try {
        const res = await verifyMobileOtp(mobileNumber, otpCode);
        const token = res.token;
        const rToken = res.refreshToken;
        let profile = res.user;

        // Parallelize fetching full profile and checking onboarding status
        const [profileResult, progressResult] = await Promise.allSettled([
          fetchCurrentUser(token),
          fetchOnboardingProgress(token),
        ]);

        if (profileResult.status === "fulfilled" && profileResult.value) {
          profile = { ...profile, ...profileResult.value };
        }

        let needsOnboarding = true;
        if (progressResult.status === "fulfilled" && progressResult.value?.is_onboarded) {
          needsOnboarding = false;
        } else if (profile.name && !profile.name.startsWith("Member (")) {
          needsOnboarding = false;
        }

        setAccessToken(token);
        setRefreshToken(rToken);
        setUser(profile);
        writeStoredSession({ accessToken: token, refreshToken: rToken, user: profile });

        if (!needsOnboarding) {
          closeAuthModal();
          if (authReturnTo) {
            const dest = authReturnTo;
            setAuthReturnTo(null);
            navigate(dest);
          }
        }

        return { user: profile, token, needsOnboarding };
      } finally {
        setIsLoading(false);
      }
    },
    [closeAuthModal, authReturnTo, navigate],
  );

  /** Completes onboarding Step 1: user name, email, referral code. */
  const completeUserProfile = useCallback(
    async ({ name, email, referralCode }) => {
      if (!accessToken) throw new Error("Authentication token missing");
      setIsLoading(true);
      try {
        const res = await submitCompleteProfile(accessToken, { name, email, referralCode });
        const updatedUser = res.user ? { ...(user || {}), ...res.user } : { ...(user || {}), name, email };
        setUser(updatedUser);
        writeStoredSession({ accessToken, refreshToken, user: updatedUser });
        return updatedUser;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, refreshToken, user],
  );

  /** Completes onboarding Step 2: user category interest preferences. */
  const saveInterests = useCallback(
    async (categoryIds) => {
      if (!accessToken) throw new Error("Authentication token missing");
      try {
        await submitCategoryPreferences(accessToken, categoryIds);
        const updatedUser = {
          ...(user || {}),
          interested_category_ids: categoryIds,
          is_onboarded: true,
        };
        setUser(updatedUser);
        writeStoredSession({ accessToken, refreshToken, user: updatedUser });
        closeAuthModal();
        if (authReturnTo) {
          const dest = authReturnTo;
          setAuthReturnTo(null);
          navigate(dest);
        }
        return updatedUser;
      } catch {
        // Close modal gracefully even if category preference save has a soft error
        closeAuthModal();
        if (authReturnTo) {
          const dest = authReturnTo;
          setAuthReturnTo(null);
          navigate(dest);
        }
      }
    },
    [accessToken, refreshToken, user, closeAuthModal, authReturnTo, navigate],
  );

  const signOut = useCallback(async () => {
    if (accessToken) {
      try {
        await logoutUser(accessToken);
      } catch {
        // Ignore network errors on logout
      }
    }
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    writeStoredSession(null);
    navigate("/", { replace: true });
  }, [accessToken, navigate]);

  /** Called by AuthCallback once a Google OAuth token exchange succeeds. */
  const completeSession = useCallback(async (token, rToken) => {
    setIsLoading(true);
    try {
      const [partnerProfile, currentUser] = await Promise.allSettled([fetchProfile(token), fetchCurrentUser(token)]);
      if (partnerProfile.status === "rejected" && currentUser.status === "rejected") throw partnerProfile.reason;
      const profile = {
        ...(partnerProfile.status === "fulfilled" ? partnerProfile.value : {}),
        ...(currentUser.status === "fulfilled" ? currentUser.value : {}),
      };
      setAccessToken(token);
      setRefreshToken(rToken || null);
      setUser(profile);
      writeStoredSession({ accessToken: token, refreshToken: rToken, user: profile });
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Re-reads the signed-in user from the server so linked Google email / phone changes show up. */
  const refreshUser = useCallback(async () => {
    if (!accessToken) return;
    try {
      const fresh = await fetchCurrentUser(accessToken);
      const nextUser = { ...(user || {}), ...fresh };
      setUser(nextUser);
      writeStoredSession({ accessToken, refreshToken, user: nextUser });
    } catch {
      // Keep the cached user if the refresh fails
    }
  }, [accessToken, refreshToken, user]);

  const updateUser = useCallback((updatedFields) => {
    setUser((prev) => {
      const nextUser = { ...(prev || {}), ...updatedFields };
      const currentStored = readStoredSession() || {};
      writeStoredSession({ ...currentStored, user: nextUser });
      return nextUser;
    });
  }, []);

  const hasIncompleteOnboarding = Boolean(
    user && (!user.is_onboarded || !user.name || user.name.startsWith("Member (")),
  );

  const value = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      isLoading,
      isSignedIn: Boolean(user),
      hasIncompleteOnboarding,
      authReturnTo,
      isAuthModalOpen,
      openAuthModal,
      closeAuthModal,
      signIn,
      signInWithGoogle,
      sendOtp,
      verifyOtp,
      completeUserProfile,
      saveInterests,
      signOut,
      completeSession,
      updateUser,
      refreshUser,
    }),
    [
      user,
      accessToken,
      refreshToken,
      isLoading,
      hasIncompleteOnboarding,
      authReturnTo,
      isAuthModalOpen,
      openAuthModal,
      closeAuthModal,
      signIn,
      signInWithGoogle,
      sendOtp,
      verifyOtp,
      completeUserProfile,
      saveInterests,
      signOut,
      completeSession,
      updateUser,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
